// Import the necessary components
import VerofreshRecord from './VerofreshRecord'
import Moment from 'moment'
import { get } from 'lodash'

class RouteLocationRecord extends VerofreshRecord {
  // The function name below is intentionally snake cased to match other
  // parameters
  // eslint-disable-next-line
  get vehicle_stop_duration () {
    return this.getVehicleStopDuration().asSeconds()
  }

  /**
   * Return a moment duration which represents the amount of time spent at a
   * stop. If the departure is not set then return a duration up to the current
   * time.
   *
   * @return Moment.duration
   */
  getVehicleStopDuration () {
    const arrival = Moment(this.vehicle_arrival)
    let departure = Moment(this.vehicle_departure)

    if (arrival.isBefore() && !departure.isValid()) {
      departure = Moment()
    }

    if (!arrival.isValid() || !departure.isValid()) {
      return undefined
    }

    return Moment.duration(departure.diff(arrival, 'milliseconds'))
  }

  /**
   * Whether route location is a missed stop.
   * if this.route not available, do not return a value
   *
   * A DC is never a missed stop, matching RouteLocation::isMissedStop() and the
   * "skip DCs" rule in Route::checkMissedStopViolations() that feeds the
   * Warnings dialog and the Missed Stops report. A DC whose arrival/departure
   * were never recorded - the vehicle left before the route's start time, so the
   * visit fell outside the detection window - used to render red with a "Missed
   * Stop" tooltip here while both of those surfaces correctly stayed silent
   * (VF-4328).
   *
   * @return {undefined|boolean}
   */
  /* eslint-disable-next-line camelcase */
  get missed_stop () {
    if (this.route) {
      const locationType = get(this, 'location.type', 'unknown')

      return locationType !== 'dc' &&
        locationType !== 'dc_loadingdock' &&
        this.route.ended &&
        this.vehicle_arrival == null &&
        this.vehicle_departure == null
    }
  }

  /**
   * Whether route location is an unplanned stop
   * @return {boolean}
   */
  /* eslint-disable-next-line camelcase */
  get unplanned_stop () {
    const unplanned = this.eta == null
    const isDistributionCenter = get(this, 'location.type', 'unknown') === 'dc'

    return unplanned && !isDistributionCenter
  }

  /**
   * Sorting function used to sort a set route locations such that the DC is
   * always at the top and then sorted by arrival time
   *
   * @param RouteLocationRecord routeLocationA
   * @param RouteLocationRecord routeLocationB
   */
  static sortByTypeAndArrivalTime (routeLocationA, routeLocationB) {
    if (routeLocationA.location.type === 'dc') { return -1 }
    if (routeLocationB.location.type === 'dc') { return 1 }
    const aDate = Moment(routeLocationA.vehicle_arrival || routeLocationA.eta)
    const bDate = Moment(routeLocationB.vehicle_arrival || routeLocationB.eta)
    return (aDate - bDate)
  }
}

const relations = {
  belongsTo: {
    Location: {
      foreignKey: 'location_id',
      localField: 'location'
    },
    Route: {
      foreignKey: 'route_id',
      localField: 'route'
    }
  }
}

export default RouteLocationRecord
export { relations }
